import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import keyBy from 'lodash/keyBy';

import {
    Configuration,
    GetAccountGroupOptimizerDetailsRequest,
    GetOptimizerDetailsRequest,
    OptimizationApi,
    OptimizationRequest,
} from '../_openapi/portfolio-optimizer';
import { defaultApiConfig } from './defaultApiConfig';

const optimizationApi = new OptimizationApi(new Configuration(defaultApiConfig));

export const useSubmitOptimization = () => {
    const toast = useToast();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (optimizationRequest: OptimizationRequest) => {
            return optimizationApi.submitOptimization({ optimizationRequest });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['optimization', 'list'] });
        },
        onError: (error: Error) => {
            toast({
                title: 'Error',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useRunAllocation = (accountGroupGuid: string, options?: { throwOnError?: boolean }) => {
    return useQuery({
        ...options,
        queryKey: ['optimization', 'allocation', accountGroupGuid],
        queryFn: async () => {
            return optimizationApi.runAllocation({ accountGroupGuid });
        },
        enabled: !!accountGroupGuid,
    });
};

export const useOptimizations = (fromDate?: string, toDate?: string) =>
    useQuery({
        queryKey: ['optimization', 'list', fromDate, toDate],
        queryFn: () =>
            optimizationApi.getOptimizationMeta({
                fromDate: fromDate || undefined,
                toDate: toDate || undefined,
            }),
        refetchInterval: 10000, // 10 sec
    });

export const useOptimizationResult = (optimizationId: number) =>
    useQuery({
        queryKey: ['optimization', optimizationId, 'result'],
        queryFn: () => optimizationApi.getOptimizationResult({ optimizationId }),
    });

export const useOptimizationMeta = (optimizationId?: number) =>
    useQuery({
        queryKey: ['optimization', optimizationId, 'meta'],
        queryFn: () => optimizationApi.getSingleOptimizationMeta({ optimizationId: optimizationId! }),
        enabled: !!optimizationId,
    });

export const useOptimizerDetails = (request: GetOptimizerDetailsRequest) =>
    useQuery({
        queryKey: ['optimization', request.optimizationId, 'portfolio', request.pId, 'optimizerDetails'],
        queryFn: () => optimizationApi.getOptimizerDetails(request),
        enabled: !!request.optimizationId && !!request.pId,
    });

export const useAccountGroupOptimizerDetails = (request: GetAccountGroupOptimizerDetailsRequest, enabled = true) =>
    useQuery({
        queryKey: [
            'optimization',
            request.optimizationId,
            'accountGroup',
            request.accountGroupGuid,
            'optimizerDetails',
        ],
        queryFn: () => optimizationApi.getAccountGroupOptimizerDetails(request),
        enabled: enabled && !!request.optimizationId && !!request.accountGroupGuid,
    });

export const useCustomAssetLifecycle = (optimizationId: number, pId: string, enabled = true) =>
    useQuery({
        queryKey: ['optimization', optimizationId, 'portfolio', pId, 'customAssetLifecycle'],
        queryFn: () => optimizationApi.getCustomAssetLifecycle({ optimizationId, pId }),
        enabled: enabled && !!optimizationId && !!pId,
        retry: false,
    });
